import * as React from 'react';
import { useCallback } from 'react';
import { css } from '@emotion/core';
import LocalDiv from '../../../localization/LocalDiv';
import DevActionForm from './DevActionForm';
import CompletedCard from './CompletionCard';
import StatusCard from './StatusCard';
import { DevPlanAction, DevPlanFieldAndAnswer } from '../models';
import { FLEX_ADAPTIVE } from '../../../layout/flexBox';
import { FORM_COLUMN } from '../../../forms/styles/form';
import Card from '../../../layout/card/Card';
import { useMsgText } from '../../../localization/hooks';
import { Str } from '../../../localization/translationKeys';
import Spacer from '../../../layout/Spacer';
import { COLORS } from '../../../globalStyle/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { useToggle } from '../../../core/hooks/useToggle';
import ConfirmationModal from '../../../layout/modal/ConfirmationModal';
import { useDispatch } from 'react-redux';
import { deleteDevPlanActionAction } from './api/deleteDevPlanActionAction';
import { Routes } from '../../../navigation/routes';
import { onMobileDevice } from '../../../globalStyle/mediaQueries';
import { useWindowSize } from '../../../core/hooks/useWindowSize';
import MobileActionBtns from './MobileActionBtns';
import { useAppState } from '../../../core/redux/hooks';
import DevItemComments from '../collaboration/DevItemComments';
import { DevItemComment } from '../collaboration/models';


const styles = {
  root:             [
    FLEX_ADAPTIVE,
    css`
      transition: all .5s ease;

      ${onMobileDevice} {
        background-color: ${COLORS.LIGHTEST_GREY};
      }
    `,
  ],
  actionForm:       css`
    flex-grow: 1;
    background-color: ${COLORS.LIGHTEST_GREY};
    padding: 15px;
  `,
  formHeader:       {
    root:    css`
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-bottom: 35px;
    `,
    title:   css`
      text-align: start;
      margin: 0;
      color: ${COLORS.DARK_GREY};
    `,
    iconBtn: css`
      border: none;
      padding: 0;
      transition: opacity .25s ease;
      
      &:hover { 
        opacity: .4;
        cursor: pointer;
      }
    `,
    icon:    css`
      font-size: 14px;
      color: ${COLORS.DARK_GREY};
    `,
  },
  actionCards:      [
    FORM_COLUMN,
    css`
      width: 255px;
      flex-basis: 255px;
    `,
  ],
  mobileActionBtns: css`
    margin: 0 15px 20px;
  `,
};


type Props = {
  routes: Routes;
  itemTitle: string;
  actionNumber: number;
  action: DevPlanAction | undefined;
  fieldsAnswers: DevPlanFieldAndAnswer[];

  collaborationEnabled: boolean;
  isParticipant: boolean;
  comments: DevItemComment[];
  addComment: (comment: string) => void;
  updateComment: (commentId: number, comment: string) => void;
  onCommentAdded: (commentId: string) => void;

  devItemCompetencyId?: number;

  className?: string;
};


/**
 * DevActionView: a component that renders a dev plan's dev item's action including form to update that action.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const DevActionView: React.FC<Props> = props => {
  const dispatch = useDispatch();

  const { isMobile } = useWindowSize();

  const title = useMsgText(Str.ActionNumber, '', { 'number': props.actionNumber });

  const deleteConfirmation = useMsgText(
    Str.ConfirmRemoveAction,
    '',
    { 'number': props.actionNumber, 'itemTitle': props.itemTitle },
  );

  const deleteActionLabel = useMsgText(Str.DeleteActionNumber, '', { 'number': props.actionNumber });

  const projectId        = useAppState(state => state.projectSummary?.projectId ?? 0);
  const devPlanProjectId = useAppState(state => state.projectSummary?.devPlanProjectId || projectId);

  const [confirmDelete, toggleConfirmDelete] = useToggle(false);

  const onConfirmDelete = useCallback(
    () => {
      dispatch(deleteDevPlanActionAction(
        devPlanProjectId, props.action?.devItemId || -1, props.action?.devActionId || -1,
      ));
      toggleConfirmDelete();
    },
    [props, devPlanProjectId, dispatch, toggleConfirmDelete],
  );

  const dir = useAppState(state => state.locale.textDirection);

  if (!props.action) {
    return <div className={props.className} />;
  }

  return (
    <LocalDiv css={styles.root} className={props.className}>
      {/* Action Form */}
      <Card css={styles.actionForm}>
        <LocalDiv css={styles.formHeader.root}>
          <h4 css={styles.formHeader.title} dir={dir}>{title}</h4>
          <Spacer sizePx={12} />
          {
            props.isParticipant && props.comments.length === 0 &&  (
              <button onClick={toggleConfirmDelete} css={styles.formHeader.iconBtn} aria-label={deleteActionLabel}>
                <FontAwesomeIcon icon={faTrash} css={styles.formHeader.icon} />
              </button>
            )
          }
        </LocalDiv>
        <DevActionForm
          fieldsAnswers={props.fieldsAnswers}
          devItemCompetencyId={props.devItemCompetencyId}
          isParticipant={props.isParticipant}
        />
        {
          props.collaborationEnabled && (
            <>
              <Spacer sizePx={50} />
              <DevItemComments
                projectId={devPlanProjectId}
                devItemId={props.action?.devItemId || -1}
                devActionId={props.action?.devActionId || -1}
                comments={props.comments}
                addComment={props.addComment}
                updateComment={props.updateComment}
                onCommentAdded={props.onCommentAdded}
              />
            </>
          )
        }
      </Card>

      <Spacer />

      {
        isMobile
          ? <MobileActionBtns action={props.action} css={styles.mobileActionBtns} />
          : (
            <div css={styles.actionCards}>
              <CompletedCard
                actionId={props.action.devActionId}
                devPlanItemId={props.action.devItemId}
                completeBy={props.action.goalDate ? new Date(props.action.goalDate) : new Date()}
                isParticipant={props.isParticipant}
              />
              <StatusCard action={props.action} isParticipant={props.isParticipant} />
            </div>
          )
      }

      {
        confirmDelete && (
          <ConfirmationModal
            confirmationTxt={deleteConfirmation}
            primaryActionStr={Str.No}
            secondaryActionStr={Str.Yes}
            onSelectPrimary={toggleConfirmDelete}
            onSelectSecondary={onConfirmDelete}
            onDismiss={toggleConfirmDelete}
          />
        )
      }

    </LocalDiv>
  );
};

export default DevActionView;
